package ai.beakman.undercover.online

import ai.beakman.undercover.Identity
import ai.beakman.undercover.data.RoomRepository
import ai.beakman.undercover.data.Themes
import ai.beakman.undercover.game.GameLogic
import ai.beakman.undercover.i18n.I18n
import ai.beakman.undercover.i18n.Language
import ai.beakman.undercover.model.Player
import ai.beakman.undercover.model.Room
import ai.beakman.undercover.model.Stage
import ai.beakman.undercover.model.Theme
import ai.beakman.undercover.model.Vote
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch

private fun nowMs(): Long = nowMillis()

/**
 * Estado realtime de uma sala online (mobile). A UI coleta os StateFlows com
 * `collectAsState()`. Ações têm versões `*Async` (fire-and-forget no scope) para uso
 * direto em onClick. O host dirige as transições de stage; cada device observa a sala.
 */
class OnlineGameState(
    val code: String,
    val scope: CoroutineScope,
    private val repo: RoomRepository = RoomRepository(),
) {
    val clientId: String = Identity.clientId

    val room: StateFlow<Room?> =
        repo.observeRoom(code).stateIn(scope, SharingStarted.Eagerly, null)
    val players: StateFlow<List<Player>> =
        repo.observePlayers(code).stateIn(scope, SharingStarted.Eagerly, emptyList())
    val votes: StateFlow<List<Vote>> =
        repo.observeVotes(code).stateIn(scope, SharingStarted.Eagerly, emptyList())

    fun meIn(players: List<Player>): Player? = players.firstOrNull { it.clientId == clientId }
    fun isHost(room: Room?): Boolean = room?.hostClientId == clientId

    // --- ações suspensas ---
    suspend fun createRoom(hostName: String, theme: Theme, numInfiltrators: Int, timerSeconds: Int) {
        repo.createRoom(
            Room(
                code = code, hostClientId = clientId, lang = I18n.current.code,
                themeId = theme.id, themeName = theme.name, themeEmoji = theme.emoji,
                numInfiltrators = numInfiltrators, timerSeconds = timerSeconds,
                stage = Stage.LOBBY, createdAt = nowMs(),
            ),
        )
        join(hostName)
    }

    suspend fun join(name: String) =
        repo.joinPlayer(code, Player(clientId = clientId, name = name, ready = false, joinOrder = nowMs()))

    suspend fun setReady() = repo.setReady(code, clientId, true)

    suspend fun setInfiltrators(room: Room, n: Int) = repo.saveRoom(room.copy(numInfiltrators = n))

    suspend fun startGame(room: Room, players: List<Player>) {
        // Resolve o tema no idioma DA SALA (snapshot), não no idioma do device do host.
        val lang = Language.fromCode(room.lang)
        val theme = Themes.byId(room.themeId, lang) ?: Themes.localized(lang).first()
        val ids = players.sortedBy { it.joinOrder }.map { it.clientId }
        val setup = GameLogic.setupRound(theme, ids, room.numInfiltrators)
        repo.resetAllReady(code)
        repo.clearVotes(code)
        repo.saveRoom(
            room.copy(
                stage = Stage.REVEAL, word = setup.word, hint = setup.hint,
                infiltratorPlayerIds = setup.infiltratorIds, timerStartedAt = null, round = room.round + 1,
            ),
        )
    }

    suspend fun startTimer(room: Room) =
        repo.saveRoom(room.copy(stage = Stage.TIMER, timerStartedAt = nowMs()))

    suspend fun goToVote(room: Room) = repo.saveRoom(room.copy(stage = Stage.VOTE))
    suspend fun showResult(room: Room) = repo.saveRoom(room.copy(stage = Stage.RESULT))

    suspend fun castVote(targetIds: List<String>) =
        repo.castVote(code, Vote(voterId = clientId, votedForIds = targetIds))

    suspend fun playAgain(room: Room) {
        repo.resetAllReady(code)
        repo.clearVotes(code)
        repo.saveRoom(
            room.copy(
                stage = Stage.LOBBY, word = null, hint = null,
                infiltratorPlayerIds = emptyList(), timerStartedAt = null,
            ),
        )
    }

    /** Saída explícita da sala. Host no lobby encerra a sala pra todos; senão remove só o jogador. */
    suspend fun leave(room: Room?) {
        if (room != null && isHost(room) && room.stage == Stage.LOBBY) repo.deleteRoom(code)
        else repo.leaveRoom(code, clientId)
    }

    // --- wrappers fire-and-forget para usar direto em onClick ---
    fun createRoomAsync(hostName: String, theme: Theme, n: Int, t: Int): Job =
        scope.launch { createRoom(hostName, theme, n, t) }
    fun joinAsync(name: String): Job = scope.launch { join(name) }
    fun readyAsync(): Job = scope.launch { setReady() }
    fun setInfiltratorsAsync(room: Room, n: Int): Job = scope.launch { setInfiltrators(room, n) }
    fun startGameAsync(room: Room, players: List<Player>): Job = scope.launch { startGame(room, players) }
    fun startTimerAsync(room: Room): Job = scope.launch { startTimer(room) }
    fun goToVoteAsync(room: Room): Job = scope.launch { goToVote(room) }
    fun showResultAsync(room: Room): Job = scope.launch { showResult(room) }
    fun castVoteAsync(targetIds: List<String>): Job = scope.launch { castVote(targetIds) }
    fun playAgainAsync(room: Room): Job = scope.launch { playAgain(room) }
    fun leaveAsync(room: Room?): Job = scope.launch { runCatching { leave(room) } }
}
